/**
 * The manpower conflict panel (LED-1156): a manpower entry from this walk for
 * a company that already has a row on the day's Procore log, and the
 * reviewer's choice of how the two land.
 *
 * ## The protocol is the phone's, not a new one
 *
 * The four choices are the backend's own `ManpowerMergeResolution`s —
 * `both | update | add | keep` (`shared/manpowerMerge.ts`) — and the option
 * set per variant is the phone's (`manpowerMergeSegmentOrder`): Both exists
 * only when the two rows are at DIFFERENT locations, where it is also the
 * suggestion; at the same location Both is hidden and Update leads. What the
 * host submits on Accept is `manpowerMergeSubmission`'s answer, the same
 * function the phone's `commitManpowerMergeDraft` calls — see
 * `manpowerConflictSubmission` below.
 *
 * ## What it draws (design "Review card", conflict state)
 *
 * One header line with Procore's mark, the segmented choice, then a bubble
 * per record — "Already in Procore" and "This walk", each with its own pills.
 * A green outline and a tag (STAYS, ADDED, REPLACES PROCORE) mark what goes
 * to Procore; a replaced or unsent record collapses to one dashed, struck
 * line. Add draws a single Combined bubble. One plain sentence states the
 * outcome, and the host's action button reads `manpowerConflictActionLabel`.
 *
 * Reusable: the Review card says "Accept …", the Daily log detail pane says
 * "Save …" (`verb`).
 */
import type { CSSProperties } from "react";
import { Check } from "lucide-react";
import {
  resolveManpowerWorkers,
  type ManpowerMergeResolution,
  type ManpowerMergeVariant,
} from "../../../../backend/src/shared/manpowerMerge";
import { PROCORE_MARK_URL } from "../assets/procoreMark";
import { C, FONT_MONO, RV } from "../tokens";

export type { ManpowerMergeResolution, ManpowerMergeVariant };

/*
 * The phone's three helpers from `frontend/lib/manpowerMergeView.ts`
 * (`manpowerMergeSegmentOrder`, `coerceManpowerMergeResolution`,
 * `manpowerMergeSubmission`), restated here rather than imported: that module
 * pulls `date-fns` through `dateTime.ts`, which the console bundle does not
 * resolve from `backend/`. `ManpowerConflictPanel.test.tsx` asserts these
 * agree with the phone's own, choice by choice, so they cannot drift.
 */
const SAME_LOCATION_ORDER: ManpowerMergeResolution[] = ["update", "add", "keep"];
const DIFFERENT_LOCATION_ORDER: ManpowerMergeResolution[] = ["both", "update", "add", "keep"];

function manpowerMergeSegmentOrder(variant: ManpowerMergeVariant): ManpowerMergeResolution[] {
  return variant === "different_location" ? DIFFERENT_LOCATION_ORDER : SAME_LOCATION_ORDER;
}

function coerceManpowerMergeResolution(resolution: ManpowerMergeResolution, variant: ManpowerMergeVariant): ManpowerMergeResolution {
  const order = manpowerMergeSegmentOrder(variant);
  return order.includes(resolution) ? resolution : order[0]!;
}

function manpowerMergeSubmission(args: {
  resolution: ManpowerMergeResolution;
  target: { remoteId: string; workers: number | null };
  walkWorkers: number | null;
}): { mergeResolution: ManpowerMergeResolution; mergeTargetRemoteId: string; workers?: number | null; mergeWalkWorkersBefore?: number | null } {
  if (args.resolution === "add") {
    return {
      mergeResolution: "update",
      mergeTargetRemoteId: args.target.remoteId,
      workers: resolveManpowerWorkers("add", args.target.workers, args.walkWorkers),
      mergeWalkWorkersBefore: args.walkWorkers,
    };
  }
  return { mergeResolution: args.resolution, mergeTargetRemoteId: args.target.remoteId };
}

/** One manpower row as the panel draws it. */
export interface ManpowerConflictRecord {
  company: string;
  workers: number | null;
  hours: number | null;
  location: string | null;
}

export type ManpowerConflictVerb = "Accept" | "Save";

const CHOICE_LABEL: Record<ManpowerMergeResolution, string> = {
  both: "Both",
  update: "Update",
  add: "Add",
  keep: "Keep",
};

/** The choices offered, in order; the first is the variant's default. */
export function manpowerConflictChoices(variant: ManpowerMergeVariant): ManpowerMergeResolution[] {
  return manpowerMergeSegmentOrder(variant);
}

/** The choice in effect: the reviewer's, else the suggestion, valid for the variant. */
export function manpowerConflictChoice(
  variant: ManpowerMergeVariant,
  requested: ManpowerMergeResolution | null | undefined,
  suggested?: ManpowerMergeResolution,
): ManpowerMergeResolution {
  return coerceManpowerMergeResolution(requested ?? suggested ?? manpowerMergeSegmentOrder(variant)[0]!, variant);
}

/** The action button's words for a choice. */
export function manpowerConflictActionLabel(choice: ManpowerMergeResolution, verb: ManpowerConflictVerb = "Accept"): string {
  if (verb === "Save") {
    return { both: "Save both", update: "Save & update Procore", add: "Save & add", keep: "Save & keep Procore" }[choice];
  }
  return { both: "Accept both", update: "Accept & update", add: "Accept & add", keep: "Accept & keep" }[choice];
}

function n(value: number | null): string {
  return value === null ? "—" : String(value);
}

function total(workers: number | null, hours: number | null): number | null {
  return workers === null || hours === null ? null : workers * hours;
}

/** `4 × 8 hrs = 32 · Level 1` */
export function manpowerLine(record: ManpowerConflictRecord): string {
  return `${n(record.workers)} × ${n(record.hours)} hrs = ${n(total(record.workers, record.hours))}${record.location ? ` · ${record.location}` : ""}`;
}

export interface ManpowerConflictBubble {
  source: "procore" | "walk" | "combined";
  /** `full` draws its pills outlined green; `out` is one struck line. */
  state: "full" | "out";
  kicker: string;
  /** STAYS, ADDED, REPLACES PROCORE, ONE ROW IN PROCORE / REPLACED, NOT SENT. */
  tag: string;
  pills: Array<{ key?: string; value: string }>;
  line: string;
}

function pills(company: string, workers: string, hours: number | null, totalValue: number | null, location: string | null) {
  return [
    { value: company },
    { key: "Workers", value: workers },
    { key: "Hours", value: n(hours) },
    { key: "Total", value: n(totalValue) },
    ...(location ? [{ value: location }] : []),
  ];
}

/** The bubbles and the outcome sentence for one choice — pure, so it can be
 *  asserted without a DOM. */
export function manpowerConflictView(
  choice: ManpowerMergeResolution,
  existing: ManpowerConflictRecord,
  walk: ManpowerConflictRecord,
): { bubbles: ManpowerConflictBubble[]; outcome: string } {
  const full = (source: "procore" | "walk", record: ManpowerConflictRecord, tag: string): ManpowerConflictBubble => ({
    source,
    state: "full",
    kicker: source === "procore" ? "Already in Procore" : "This walk",
    tag,
    pills: pills(record.company, n(record.workers), record.hours, total(record.workers, record.hours), record.location),
    line: manpowerLine(record),
  });
  const out = (source: "procore" | "walk", record: ManpowerConflictRecord, tag: string): ManpowerConflictBubble => ({
    source,
    state: "out",
    kicker: source === "procore" ? "Procore" : "This walk",
    tag,
    pills: [],
    line: manpowerLine(record),
  });
  const at = (record: ManpowerConflictRecord) => (record.location ? ` at ${record.location}` : "");
  switch (choice) {
    case "both":
      return {
        bubbles: [full("procore", existing, "Stays"), full("walk", walk, "Added")],
        outcome: `Two rows go to Procore — ${n(existing.workers)}${at(existing)} and ${n(walk.workers)}${at(walk)}.`,
      };
    case "update": {
      const t = total(walk.workers, walk.hours);
      return {
        bubbles: [out("procore", existing, "Replaced"), full("walk", walk, "Replaces Procore")],
        outcome: `Procore’s row is overwritten: ${n(walk.workers)} × ${n(walk.hours)} hrs = ${n(t)}.`,
      };
    }
    case "add": {
      const sum = resolveManpowerWorkers("add", existing.workers, walk.workers);
      const hours = walk.hours ?? existing.hours;
      const t = total(sum, hours);
      const workers = existing.workers !== null && walk.workers !== null
        ? `${existing.workers} + ${walk.workers} = ${sum}`
        : n(sum);
      return {
        bubbles: [{
          source: "combined",
          state: "full",
          kicker: "Combined",
          tag: "One row in Procore",
          pills: pills(walk.company || existing.company, workers, hours, t, walk.location ?? existing.location),
          line: "",
        }],
        outcome: `One row with the counts added: ${n(sum)} × ${n(hours)} hrs = ${n(t)}.`,
      };
    }
    case "keep":
      return {
        bubbles: [full("procore", existing, "Stays"), out("walk", walk, "Not sent")],
        outcome: "Procore is left as is. This entry stays in Led AI only.",
      };
  }
}

/**
 * What Accept sends for a choice: the phone's `manpowerMergeSubmission`, made
 * into the review patch's keys. `add` is resolved into the record — Workers
 * becomes the sum and the decision is stored as an update — exactly as on the
 * phone, so the number the bubble showed is the number written.
 *
 * @param fieldValues The record's field values as they will be submitted
 *   (the draft's, else the record's own), so an `add` carries the whole set.
 */
export function manpowerConflictSubmission(args: {
  choice: ManpowerMergeResolution;
  targetRemoteId: string;
  existingWorkers: number | null;
  walkWorkers: number | null;
  fieldValues?: Record<string, unknown>;
}): Record<string, unknown> {
  const { workers, ...decision } = manpowerMergeSubmission({
    resolution: args.choice,
    target: { remoteId: args.targetRemoteId, workers: args.existingWorkers },
    walkWorkers: args.walkWorkers,
  });
  return {
    ...decision,
    ...(workers !== undefined ? { fieldValues: { ...(args.fieldValues ?? {}), workers } } : {}),
  };
}

export interface ManpowerConflictPanelProps {
  company: string;
  /** Where the existing row is — "today's Procore log", "the Oct 2 Procore log". */
  where?: string;
  existing: ManpowerConflictRecord;
  walk: ManpowerConflictRecord;
  variant: ManpowerMergeVariant;
  choice: ManpowerMergeResolution;
  onChoice: (choice: ManpowerMergeResolution) => void;
  disabled?: boolean;
  testId?: string;
}

const MONO: CSSProperties = { fontFamily: FONT_MONO, fontWeight: 700, textTransform: "uppercase" };

export function ManpowerConflictPanel({ company, where = "today's Procore log", existing, walk, variant, choice, onChoice, disabled, testId = "manpower-conflict" }: ManpowerConflictPanelProps) {
  const choices = manpowerConflictChoices(variant);
  const { bubbles, outcome } = manpowerConflictView(choice, existing, walk);
  return (
    <div data-testid={testId} data-variant={variant} data-choice={choice} style={{ padding: 12, borderRadius: 14, background: RV.recess }}>
      {/* The header line, then the choice under it (design "Review card"). */}
      <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 8 }}>
        <span style={{ display: "flex", alignItems: "flex-start", gap: 7, fontSize: 12.5, lineHeight: 1.4, color: RV.ink2, minWidth: 0 }}>
          <img src={PROCORE_MARK_URL} alt="" style={{ width: 13, height: 13, borderRadius: 3, display: "block", flex: "none", marginTop: 2 }} />
          <span data-testid={`${testId}-head`}>{`${company} already has manpower on ${where}`}</span>
        </span>
        <div role="radiogroup" aria-label="How this entry lands in Procore" style={{ display: "inline-flex", gap: 3, padding: 3, borderRadius: 999, background: RV.segTrack, flex: "none" }}>
          {choices.map((option) => {
            const on = option === choice;
            return (
              <button
                key={option}
                type="button"
                role="radio"
                aria-checked={on}
                data-testid={`${testId}-choice`}
                data-choice={option}
                disabled={disabled}
                onClick={() => onChoice(option)}
                className="mp-choice"
                style={{
                  height: 28,
                  padding: "0 12px",
                  borderRadius: 999,
                  border: 0,
                  background: on ? RV.bubble : "transparent",
                  boxShadow: on ? RV.segShadow : "none",
                  color: on ? C.ink : C.sub,
                  fontFamily: "inherit",
                  fontSize: 12.5,
                  fontWeight: 700,
                  cursor: disabled ? "default" : "pointer",
                  whiteSpace: "nowrap",
                }}
              >
                {CHOICE_LABEL[option]}
              </button>
            );
          })}
        </div>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 10 }}>
        {bubbles.map((bubble) =>
          bubble.state === "full" ? (
            <div key={`${bubble.source}-full`} data-testid={`${testId}-bubble`} data-source={bubble.source} data-state="full" style={{ padding: "9px 11px 11px", borderRadius: 12, background: RV.bubble, border: `1.5px solid ${RV.green}` }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 7 }}>
                <span style={{ ...MONO, fontSize: 9, letterSpacing: "1.2px", color: C.sub }}>{bubble.kicker}</span>
                <span style={{ flex: 1 }} />
                <span data-testid={`${testId}-tag`} style={{ ...MONO, display: "inline-flex", alignItems: "center", gap: 4, fontSize: 8.5, letterSpacing: "1px", color: RV.greenInk, whiteSpace: "nowrap" }}>
                  <Check size={10} color={RV.green} strokeWidth={3} aria-hidden />
                  {bubble.tag}
                </span>
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {bubble.pills.map((pill, index) => (
                  <span key={index} style={{ display: "inline-flex", alignItems: "center", gap: 6, height: 30, padding: "0 11px", borderRadius: 999, background: RV.bubblePill, border: "1px solid rgba(20,17,12,.08)", fontSize: 13, whiteSpace: "nowrap" }}>
                    {pill.key && <span style={{ ...MONO, fontSize: 9, letterSpacing: "1px", color: C.sub }}>{pill.key}</span>}
                    <span style={{ fontWeight: 700 }}>{pill.value}</span>
                  </span>
                ))}
              </div>
            </div>
          ) : (
            <div key={`${bubble.source}-out`} data-testid={`${testId}-bubble`} data-source={bubble.source} data-state="out" style={{ display: "flex", alignItems: "center", gap: 9, minHeight: 34, padding: "0 11px", borderRadius: 10, background: RV.outGround, border: `1px dashed ${RV.outLine}` }}>
              <span style={{ ...MONO, flex: "none", fontSize: 8.5, letterSpacing: "1.1px", color: RV.meta }}>{bubble.kicker}</span>
              <span style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: 12.5, color: RV.meta, textDecoration: "line-through" }}>{bubble.line}</span>
              <span style={{ flex: 1 }} />
              <span style={{ ...MONO, flex: "none", fontSize: 8.5, letterSpacing: "1px", color: RV.outTag, whiteSpace: "nowrap" }}>{bubble.tag}</span>
            </div>
          ),
        )}
      </div>
      <div data-testid={`${testId}-outcome`} style={{ fontSize: 12.5, lineHeight: 1.45, color: C.sub, marginTop: 9 }}>{outcome}</div>
    </div>
  );
}
